import { Head } from '@inertiajs/react'
import { useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Select from '../components/ui/Select'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import Modal from '../components/ui/Modal'
import Alert from '../components/ui/Alert'

/**
 * Styleguide page showcasing all UI components
 */
export default function Styleguide() {
  const [inputValue, setInputValue] = useState('')
  const [textareaValue, setTextareaValue] = useState('')
  const [selectValue, setSelectValue] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const selectOptions = [
    { value: 'fr', label: 'France' },
    { value: 'be', label: 'Belgium' },
    { value: 'ch', label: 'Switzerland' }
  ]

  return (
    <MainLayout>
      <Head title="Styleguide - VitrineImmo" />

      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Design System
            </h1>
            <p className="text-lg text-gray-600">
              Composants UI réutilisables de VitrineImmo
            </p>
          </div>

          {/* Colors */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Couleurs</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="h-24 bg-primary-600 rounded-lg mb-2"></div>
                <p className="text-sm font-medium">Primary 600</p>
                <p className="text-xs text-gray-500">#2563eb</p>
              </div>

              <div>
                <div className="h-24 bg-secondary-600 rounded-lg mb-2"></div>
                <p className="text-sm font-medium">Secondary 600</p>
                <p className="text-xs text-gray-500">#475569</p>
              </div>

              <div>
                <div className="h-24 bg-success rounded-lg mb-2"></div>
                <p className="text-sm font-medium">Success</p>
                <p className="text-xs text-gray-500">#10b981</p>
              </div>

              <div>
                <div className="h-24 bg-error rounded-lg mb-2"></div>
                <p className="text-sm font-medium">Error</p>
                <p className="text-xs text-gray-500">#ef4444</p>
              </div>
            </div>
          </section>

          {/* Buttons */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Buttons</h2>

            <div className="space-y-8">
              {/* Variants */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Variants</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="danger">Danger</Button>
                </div>
              </div>

              {/* Sizes */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Sizes</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button size="md">Medium</Button>
                  <Button size="lg">Large</Button>
                </div>
              </div>

              {/* States */}
              <div>
                <h3 className="text-lg font-semibold mb-4">States</h3>
                <div className="flex flex-wrap gap-3">
                  <Button>Default</Button>
                  <Button disabled>Disabled</Button>
                  <Button loading>Loading</Button>
                </div>
              </div>
            </div>
          </section>

          {/* Form Inputs */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Form Components</h2>

            <div className="space-y-8 max-w-md">
              {/* Input */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Input</h3>
                <div className="space-y-4">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="email@example.com"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />

                  <Input
                    label="Required Field"
                    required
                    placeholder="Enter something..."
                  />

                  <Input
                    label="With Error"
                    error="This field is required"
                    placeholder="Enter something..."
                  />

                  <Input
                    label="Disabled"
                    disabled
                    placeholder="Disabled input"
                  />
                </div>
              </div>

              {/* Textarea */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Textarea</h3>
                <Textarea
                  label="Message"
                  rows={4}
                  placeholder="Enter your message..."
                  value={textareaValue}
                  onChange={(e) => setTextareaValue(e.target.value)}
                  maxLength={200}
                  showCount
                />
              </div>

              {/* Select */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Select</h3>
                <Select
                  label="Country"
                  placeholder="Select a country"
                  options={selectOptions}
                  value={selectValue}
                  onChange={(e) => setSelectValue(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Badges */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Badges</h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-4">Variants</h3>
                <div className="flex flex-wrap gap-3">
                  <Badge variant="default">Default</Badge>
                  <Badge variant="success">Success</Badge>
                  <Badge variant="warning">Warning</Badge>
                  <Badge variant="error">Error</Badge>
                  <Badge variant="info">Info</Badge>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Sizes</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge size="sm">Small</Badge>
                  <Badge size="md">Medium</Badge>
                  <Badge size="lg">Large</Badge>
                </div>
              </div>
            </div>
          </section>

          {/* Alerts */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Alerts</h2>

            <div className="space-y-4 max-w-2xl">
              <Alert variant="success" title="Success" closable>
                Your changes have been saved successfully!
              </Alert>

              <Alert variant="warning" title="Warning" closable>
                This action cannot be undone. Please proceed with caution.
              </Alert>

              <Alert variant="error" title="Error" closable>
                An error occurred while processing your request.
              </Alert>

              <Alert variant="info" closable>
                This is an informational message without a title.
              </Alert>
            </div>
          </section>

          {/* Cards */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Cards</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <Card.Header>
                  <h3 className="text-lg font-semibold">Card with Header</h3>
                </Card.Header>
                <Card.Body>
                  <p className="text-gray-600">
                    This is a card with header, body, and footer sections.
                  </p>
                </Card.Body>
                <Card.Footer>
                  <Button size="sm">Action</Button>
                </Card.Footer>
              </Card>

              <Card hoverable>
                <Card.Body>
                  <h3 className="text-lg font-semibold mb-2">Hoverable Card</h3>
                  <p className="text-gray-600">
                    This card has a hover effect. Try hovering over it!
                  </p>
                </Card.Body>
              </Card>

              <Card variant="bordered">
                <Card.Body>
                  <h3 className="text-lg font-semibold mb-2">Bordered Card</h3>
                  <p className="text-gray-600">
                    This card uses a bordered variant instead of shadow.
                  </p>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant="success">Active</Badge>
                    <Badge variant="info">New</Badge>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Card with Badges</h3>
                  <p className="text-gray-600">
                    Cards can contain any combination of components.
                  </p>
                </Card.Body>
              </Card>
            </div>
          </section>

          {/* Modal */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Modal</h2>

            <Button onClick={() => setIsModalOpen(true)}>
              Open Modal
            </Button>

            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title="Example Modal"
              size="md"
            >
              <p className="mb-4">
                This is a modal dialog. It traps focus and can be closed by:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-600">
                <li>Clicking the close button</li>
                <li>Pressing the Escape key</li>
                <li>Clicking outside the modal</li>
              </ul>

              <div className="flex gap-3">
                <Button onClick={() => setIsModalOpen(false)}>
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </Modal>
          </section>

          {/* Typography */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Typography</h2>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold">Heading 1</h1>
              <h2 className="text-3xl font-bold">Heading 2</h2>
              <h3 className="text-2xl font-bold">Heading 3</h3>
              <h4 className="text-xl font-semibold">Heading 4</h4>
              <p className="text-base text-gray-600">
                This is a paragraph with regular text. It uses the base font size
                and gray color for better readability.
              </p>
              <p className="text-sm text-gray-500">
                This is smaller text, typically used for captions or secondary information.
              </p>
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  )
}
